<script setup>
import { useClipboard } from '@vueuse/core'
import { MainnetLoans } from '@folks-finance/algorand-sdk'
import { camelCase, startCase } from 'lodash-es'
import { FilterMatchMode } from 'primevue/api';
import DataTable from 'primevue/datatable'
import Dropdown from 'primevue/dropdown'
import Column from 'primevue/column'
import { ref, shallowRef, computed } from 'vue';

import { fmtShortCurrency, fmtPercent } from '@/format.js'
import * as api from '@/api'

const loans = shallowRef([])
const totalLoans = ref(0)
const fetchOpts = ref({})
const isLoading = ref(false)
const loanTypes = computed(() => [...Object.keys(MainnetLoans)])
const filters = ref({
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    userAddress: { value: null, matchMode: FilterMatchMode.CONTAINS },
    escrowAddress: { value: null, matchMode: FilterMatchMode.CONTAINS },
    loanAppName: { value: null, matchMode: FilterMatchMode.EQUALS },
});
const { copy } = useClipboard()

function fmtCurrency (val) {
    if (val > 1) {
        return fmtShortCurrency(val)
    } else if (val > 0) {
        return fmtShortCurrency(val, {minimumSignificantDigits: 2})
    } else {
        return '$0'
    }
}

function fmtLoanType (val) {
    return startCase(camelCase(val)).replace('Algo', 'ALGO')
}
function utilizationClass (val) {
    if (val >= 1) {
        return 'has-text-danger'
    } else if (val >= .9) {
        return 'has-text-warning'
    }
}

const defaultRows = 20
const defaultSortOrder = -1
const defaultSortField = 'collateralUSD'

const fetchData = async (event, type) => {
    isLoading.value = true
    try {
        const opts = {
            offset: event?.first ?? 0,
            limit: event?.rows ?? defaultRows,
            sortField: event?.sortField ?? defaultSortField,
            sortOrder: event?.sortOrder ?? defaultSortOrder,
            loanAppName: event?.filters.loanAppName.value ?? null,
        }
        fetchOpts.value = opts

        const data = await api.fetchFolksLoans(opts)
        loans.value = data.loans
        totalLoans.value = data.total
    } finally {
        isLoading.value = false
    }
}

fetchData()

</script>

<template>
<div>
    Folks Loans
    <div>
        <DataTable
            :value="loans"
            v-model:filters="filters"
            :loading="isLoading"
            lazy
            filterDisplay="row"
            :sortField="defaultSortField"
            :sortOrder="defaultSortOrder"
            :rows="defaultRows"
            :rowsPerPageOptions="[10, 20, 50]"
            :totalRecords="totalLoans"
            @page="fetchData($event, 'page')"
            @sort="fetchData($event, 'sort')"
            @filter="fetchData($event, 'filter')"
            paginator
            paginatorTemplate="CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
            currentPageReportTemplate="{first} - {last} of {totalRecords}"
            dataKey="escrowAddress">
            <Column field="escrowAddress">
                <template #body="{ data }">
                    <RouterLink :to="`/folks-finance/loans/${data.escrowAddress}`" class="has-text-white">
                        <fa-icon icon="fa-solid fa-link"/>
                    </RouterLink>
                </template>
            </Column>
            <Column
                field="userAddress"
                :showClearButton="false"
                :showFilterMenu="false">
                <template #header>
                    User Address
                </template>
                <template #body="{ data }">
                    <span @click="copy(data.userAddress)" class="monospace copy-cell">
                        {{ data.userAddress.slice(0, 6) }}
                        <fa-icon icon="fa-regular fa-copy"/>
                    </span>
                </template>
            </Column>
            <Column
                field="escrowAddress"
                :showClearButton="false"
                :showFilterMenu="false">
                <template #header>
                    Loan Address
                </template>
                <template #body="{ data }">
                    <span @click="copy(data.escrowAddress)" class="monospace copy-cell">
                        {{ data.escrowAddress.slice(0, 6) }}
                        <fa-icon icon="fa-regular fa-copy"/>
                    </span>
                </template>
            </Column>
            <Column field="collateralUSD" sortable>
                <template #header>
                    Collateral $
                </template>
                <template #body="{ data }">
                    <span class="monospace">
                        {{ fmtCurrency(data.collateralUSD) }}
                    </span>
                </template>
            </Column>
            <Column field="borrowUSD" sortable>
                <template #header>
                    Borrow $
                </template>
                <template #body="{ data }">
                    <span class="monospace">
                        {{ fmtCurrency(data.borrowUSD) }}
                    </span>
                </template>
            </Column>
            <Column field="borrowUtilization" sortable>
                <template #header>
                    <div>
                        Borrow
                        <span class="text-no-wrap">
                            Utilization
                            <span v-tooltip.top="'Liquidation occurs at 100% utilization.'">
                                <fa-icon icon="fa-solid fa-circle-question" />
                            </span>
                        </span>

                    </div>
                </template>
                <template #body="{ data }">
                    <span class="monospace" :class="utilizationClass(data.borrowUtilization)">
                        {{ fmtPercent(data.borrowUtilization) }}
                    </span>
                </template>
            </Column>
            <Column
                field="loanAppName"
                header="Type"
                :showFilterMenu="false"
                :showClearButton="false">
                sortable
                <template #filter="{ filterModel, filterCallback }">
                    <Dropdown
                        v-model="filterModel.value"
                        @change="filterCallback()"
                        :options="loanTypes"
                        placeholder="Select Type"
                        class="p-column-filter"
                        inputClass="p-inputtext-sm"
                        :style="{'max-width': '220px'}"
                        :showClear="true">
                        <template #value="slotProps">
                            <div v-if="slotProps.value">
                                {{  fmtLoanType(slotProps.value) }}
                            </div>
                            <span v-else>
                                {{ slotProps.placeholder }}
                            </span>
                        </template>
                        <template #option="slotProps">
                            {{  fmtLoanType(slotProps.option) }}
                        </template>
                    </Dropdown>
                </template>
                <template #body="{ data }">
                    {{  fmtLoanType(data.loanAppName) }}
                </template>
            </Column>
        </DataTable>
    </div>
</div>
</template>

<style lang="scss" scoped>
.monospace {
    font-family: 'Roboto Mono', sans-serif;
}

.fa-link {
    cursor: pointer;
    transition: $default-transition-duration;
    &:hover {
        color: $link;
    }
}

.copy-cell {
    cursor: pointer;
    .fa-copy {
        transition: $default-transition-duration;
        opacity: 0;
    }
    &:hover .fa-copy {
        opacity: 1;
    }
}

:deep(table) {
    font-size: 0.9rem;
    border-radius: $default-border-radius $default-border-radius 0 0;
    overflow: hidden;
    font-family: 'Roboto', sans-serif;
}

:deep(.p-column-header-content) {
    font-weight: bold;
}

:deep(td),
:deep(th) {
    padding: .5rem .5rem;
    background: $scheme-main;
}

:deep(.p-dropdown) {
    background: $alt-background-color;
}

:global(.p-dropdown-panel) {
    background: $alt-background-color;
}

:global(.p-dropdown-item) {
    background: $alt-background-color;
}

:global(.p-dropdown-item:hover) {
    background: $scheme-main-ter;
}

:global(.p-dropdown-item.p-highlight) {
    background: $scheme-main-ter;
}

:deep(.p-paginator) {
    background: $scheme-main;
    .p-paginator-page.p-highlight {
        background: $scheme-main-ter;
    }
}

:deep(.p-sortable-column.p-highlight) {
    background: $scheme-main;
}

:deep(.p-paginator-current) {
    min-width: 170px;
    justify-content: start;
    margin-right: auto;
}
:deep(.p-paginator-rpp-options) {
    margin-left: auto;
}
</style>
